import React from 'react';
import { motion } from 'framer-motion';
import { useStory } from '../../context/StoryContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { GiBroadsword, GiShield, GiHealthPotion, GiRun } from 'react-icons/gi';

const CombatOverlay = () => {
  const { state, endCombat, addCombatLog, submitPassage } = useStory();
  const { combat, character } = state;

  if (!combat.inCombat) return null;

  const playerStats = character.stats || { level: 1, health: 100, mana: 50, strength: 10, agility: 10, intelligence: 10, experience: 0 };
  const enemyStats = combat.enemy?.stats || { health: 100, maxHealth: 100, level: 1 };

  const handleAction = (actionType) => {
    let message = '';
    switch (actionType) {
      case 'attack':
        message = `[Attack] ${character.name || 'You'} attacked ${combat.enemy?.name || 'the enemy'} with a strike!`;
        break;
      case 'defend':
        message = `[Defend] ${character.name || 'You'} took a defensive stance.`;
        break;
      case 'item':
        message = `[Item] ${character.name || 'You'} used a healing potion.`;
        break;
      case 'flee':
        message = `[Flee] ${character.name || 'You'} attempted to run away!`;
        break;
      default:
        break;
    }
    addCombatLog(message);
    submitPassage(message, character);
  };

  return (
    <div className="bg-gray-900 border-t-2 border-red-500/50 p-4 shadow-inner relative overflow-hidden">
      <div className="absolute inset-0 bg-red-900/10 z-0 pointer-events-none"></div>
      <div className="max-w-4xl mx-auto relative z-10">

        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-red-500 font-serif flex items-center gap-2">
            <SafeIcon icon={GiBroadsword} className="text-2xl" /> Combat Encounter
          </h3>
          <button onClick={endCombat} className="text-xs text-gray-400 hover:text-white transition-colors">
            [End Encounter]
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* Player Stats */}
          <div className="bg-gray-800 p-4 rounded-xl border border-gray-700">
            <h4 className="text-lg font-bold text-amber-500 mb-2">{character.name || 'You'} (Lvl {playerStats.level})</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <SafeIcon icon={FiIcons.FiHeart} className="text-red-400" /> Health: {playerStats.health}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <SafeIcon icon={FiIcons.FiZap} className="text-blue-400" /> Mana: {playerStats.mana}
              </div>
              <div className="grid grid-cols-3 gap-1 mt-2 text-xs text-gray-400">
                <div>STR: {playerStats.strength}</div>
                <div>AGI: {playerStats.agility}</div>
                <div>INT: {playerStats.intelligence}</div>
              </div>
            </div>
          </div>

          {/* Combat Log */}
          <div className="bg-gray-950 p-3 rounded-xl border border-gray-800 h-32 overflow-y-auto font-mono text-xs">
            {combat.combatLog.map((log, i) => (
              <div key={i} className="text-gray-400 mb-1">{'>'} {log}</div>
            ))}
            {combat.combatLog.length === 0 && <div className="text-gray-600 italic">Combat started...</div>}
          </div>

          {/* Enemy Stats */}
          <div className="bg-gray-800 p-4 rounded-xl border border-red-900/50">
            <h4 className="text-lg font-bold text-red-500 mb-2">{combat.enemy?.name || 'Enemy'} (Lvl {enemyStats.level})</h4>
            <div className="space-y-2">
               <div className="flex items-center gap-2 text-sm text-gray-300">
                <SafeIcon icon={FiIcons.FiHeart} className="text-red-400" /> Health: {enemyStats.health} / {enemyStats.maxHealth || enemyStats.health}
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2 mt-1">
                <div className="bg-red-500 h-2 rounded-full" style={{ width: `${Math.max(0, (enemyStats.health / (enemyStats.maxHealth || enemyStats.health)) * 100)}%` }}></div>
              </div>
            </div>
          </div>

        </div>

        {/* Action Bar */}
        <div className="flex justify-center gap-4 mt-6">
          <button onClick={() => handleAction('attack')} className="flex flex-col items-center gap-1 p-3 bg-red-900/30 hover:bg-red-800/50 text-red-400 rounded-xl transition-all border border-red-800/50 min-w-[80px]">
            <SafeIcon icon={GiBroadsword} className="text-2xl" />
            <span className="text-xs font-bold uppercase">Attack</span>
          </button>
          <button onClick={() => handleAction('defend')} className="flex flex-col items-center gap-1 p-3 bg-blue-900/30 hover:bg-blue-800/50 text-blue-400 rounded-xl transition-all border border-blue-800/50 min-w-[80px]">
            <SafeIcon icon={GiShield} className="text-2xl" />
            <span className="text-xs font-bold uppercase">Defend</span>
          </button>
          <button onClick={() => handleAction('item')} className="flex flex-col items-center gap-1 p-3 bg-green-900/30 hover:bg-green-800/50 text-green-400 rounded-xl transition-all border border-green-800/50 min-w-[80px]">
            <SafeIcon icon={GiHealthPotion} className="text-2xl" />
            <span className="text-xs font-bold uppercase">Item</span>
          </button>
          <button onClick={() => handleAction('flee')} className="flex flex-col items-center gap-1 p-3 bg-gray-800 hover:bg-gray-700 text-gray-400 rounded-xl transition-all border border-gray-600 min-w-[80px]">
            <SafeIcon icon={GiRun} className="text-2xl" />
            <span className="text-xs font-bold uppercase">Flee</span>
          </button>
        </div>

      </div>
    </div>
  );
};

export default CombatOverlay;
